import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconAlert,
  IconBuilding,
  IconCheck,
  IconChevronRight,
  IconEdit,
  IconPin,
  IconPlus,
  IconSearch,
  IconShield,
  IconTrash,
  IconUsers,
} from '../../components/icons';
import { TenantDangerZone } from './TenantDangerZone';
import {
  Badge,
  Card,
  EmptyState,
  ErrorMessage,
  Field,
  Loading,
  ModuleHeader,
  Tabs,
  Toggle,
  useAsync,
  useToast,
} from '../../components/ui';
import { DsfaBadge } from '../../components/DsfaBadge';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDate } from '../../lib/format';

const LOCATION_KINDS: Record<string, string> = {
  depot: 'Betriebshof',
  warehouse: 'Lager',
  office: 'Büro',
  other: 'Sonstiges',
};

/**
 * Organisation (einmandantig seit 0.10.0).
 *
 * Oben die Kachel mit den grundlegenden Angaben, darunter die Register als
 * Reiter der Seite. Jeder Reiter trägt in seiner Kopfzeile den Knopf
 * „Bearbeiten“: Bei Stammdaten, Ansprechpartnern und Servicestandorten
 * schaltet er die Pflege frei, bei Benutzern und Objekten führt er in deren
 * Verwaltung (dort liegen die vollständigen Masken).
 */
export function TenantsPage() {
  const tenants = useAsync(() => api.get('/api/tenants'), []);
  const tenant = tenants.data?.[0];

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconBuilding width={22} height={22} />}
        title="Organisation"
        subtitle="Stammdaten, Ansprechpartner und Servicestandorte des Betriebs."
        pill="Organisation"
        tone="info"
      />

      {tenants.loading && !tenants.data ? (
        <Loading />
      ) : !tenant ? (
        <Card>
          <EmptyState icon={<IconBuilding width={20} height={20} />} title="Keine Organisation vorhanden" />
        </Card>
      ) : (
        <OrganisationDetail tenant={tenant} onSaved={() => tenants.reload()} />
      )}
    </div>
  );
}

/** Kachel mit den grundlegenden Angaben der Organisation. */
function OrganisationTile({ tenant }: { tenant: any }) {
  const anschrift = [[tenant.street, tenant.house_no].filter(Boolean).join(' '), [tenant.zip, tenant.city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  const zahlen: [string, number][] = [
    ['Ansprechpartner', tenant.contact_count],
    ['Servicestandorte', tenant.location_count],
    ['Benutzer', tenant.user_count],
    ['Objekte', tenant.property_count],
  ];
  return (
    <Card>
      <div className="row" style={{ gap: 14, alignItems: 'flex-start' }}>
        <span className="icon-tile info">
          <IconBuilding width={18} height={18} />
        </span>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div className="row" style={{ gap: 8 }}>
            <strong style={{ fontSize: 17 }}>{tenant.name}</strong>
            <span className="chip mono">{tenant.code}</span>
            <Badge variant={tenant.is_active ? 'success' : ''}>{tenant.is_active ? 'Aktiv' : 'Inaktiv'}</Badge>
          </div>
          <div className="small subtle" style={{ marginTop: 4 }}>
            {anschrift || 'ohne Anschrift'}
          </div>
          <div className="small subtle">
            {[tenant.contact_email, tenant.contact_phone].filter(Boolean).join(' · ') || 'ohne zentralen Kontakt'}
            {tenant.timezone ? ` · ${tenant.timezone}` : ''}
          </div>
          <div className="small subtle">seit {formatDate(tenant.created_at)}</div>
        </div>
        <div className="row" style={{ gap: 20 }}>
          {zahlen.map(([label, value]) => (
            <div key={label} style={{ textAlign: 'center' }}>
              <strong style={{ fontSize: 22 }}>{value}</strong>
              <div className="small subtle">{label}</div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Register                                                            */
/* ------------------------------------------------------------------ */

/**
 * Stammdaten, Ansprechpartner und Servicestandorte der Organisation.
 *
 * Ansprechpartner und Standorte werden sofort gespeichert, nicht erst beim
 * Verlassen des Reiters: Beide sind eigene Datensätze.
 */
function OrganisationDetail({ tenant, onSaved }: { tenant: any; onSaved: () => void }) {
  const { notify } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperadmin = Boolean(user?.isSuperadmin);
  // Die Gefahrenzone sehen nur Administratoren und Superadmins.
  const darfGefahr = isSuperadmin || Boolean(user?.roles.some((role) => role.key === 'administrator'));
  const [tab, setTab] = useState<'stamm' | 'contacts' | 'locations' | 'users' | 'properties' | 'gefahr'>('stamm');
  const tenantId: number = tenant.id;
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  // Welcher Reiter gerade bearbeitet wird – höchstens einer zugleich.
  const [editing, setEditing] = useState<string | null>(null);

  const detail = useAsync(() => api.get(`/api/tenants/${tenantId}`), [tenantId]);

  const formFromDetail = (d: any) => ({
    name: d?.name ?? '',
    code: d?.code ?? '',
    street: d?.street ?? '',
    houseNo: d?.house_no ?? '',
    zip: d?.zip ?? '',
    city: d?.city ?? '',
    contactEmail: d?.contact_email ?? '',
    contactPhone: d?.contact_phone ?? '',
    timezone: d?.timezone ?? 'Europe/Berlin',
  });
  const [form, setForm] = useState(() => formFromDetail(null));

  useEffect(() => {
    if (detail.data) setForm(formFromDetail(detail.data));
  }, [detail.data]);

  const set = (key: string, value: unknown) => setForm((current) => ({ ...current, [key]: value }));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/tenants/${tenantId}`, form);
      setEditing(null);
      detail.reload();
      onSaved();
      notify('Organisation gespeichert.', 'success');
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setForm(formFromDetail(detail.data));
    setError(null);
    setEditing(null);
  };

  const editButton = (key: string) =>
    editing === key ? (
      <button className="btn sm" onClick={() => setEditing(null)}>
        <IconCheck width={13} height={13} /> Fertig
      </button>
    ) : (
      <button className="btn sm" onClick={() => setEditing(key)}>
        <IconEdit width={13} height={13} /> Bearbeiten
      </button>
    );
  const linkButton = (to: string, title: string) => (
    <button className="btn sm" title={title} onClick={() => navigate(to)}>
      <IconEdit width={13} height={13} /> Bearbeiten
    </button>
  );
  const changed = () => {
    detail.reload();
    onSaved();
  };
  const off = editing !== 'stamm';

  return (
    <>
      <OrganisationTile tenant={tenant} />

      <Tabs
        active={tab}
        onChange={(key) => {
          setTab(key as typeof tab);
          setEditing(null);
        }}
        tabs={[
          { key: 'stamm', label: 'Stammdaten', icon: <IconBuilding width={15} height={15} /> },
          { key: 'contacts', label: 'Ansprechpartner', count: detail.data?.contacts?.length, icon: <IconUsers width={15} height={15} /> },
          { key: 'locations', label: 'Servicestandorte', count: detail.data?.locations?.length, icon: <IconPin width={15} height={15} /> },
          { key: 'users', label: 'Benutzer', count: detail.data?.users?.length, icon: <IconUsers width={15} height={15} /> },
          { key: 'properties', label: 'Objekte', count: detail.data?.properties?.length, icon: <IconBuilding width={15} height={15} /> },
          ...(darfGefahr ? [{ key: 'gefahr', label: 'Gefahrenzone', icon: <IconAlert width={15} height={15} /> }] : []),
        ]}
      />

      {tab === 'stamm' && (
        <Card
          title="Stammdaten"
          actions={
            editing === 'stamm' ? (
              <>
                <button className="btn sm" onClick={cancel} disabled={busy}>
                  Abbrechen
                </button>
                <button className="btn sm primary" onClick={save} disabled={busy || !form.name}>
                  <IconCheck width={13} height={13} /> Speichern
                </button>
              </>
            ) : (
              editButton('stamm')
            )
          }
        >
          <div className="stack">
            <ErrorMessage error={error} />
            <div className="form-grid">
              <Field label="Name" required>
                <input value={form.name} disabled={off} onChange={(event) => set('name', event.target.value)} />
              </Field>
              <Field label="Kürzel" required hint="Unveränderlich.">
                <input value={form.code} disabled />
              </Field>
            </div>

            <span className="section-title">Anschrift</span>
            <div className="address-grid">
              <Field label="Straße">
                <input value={form.street} disabled={off} onChange={(event) => set('street', event.target.value)} />
              </Field>
              <Field label="Nr.">
                <input value={form.houseNo} disabled={off} onChange={(event) => set('houseNo', event.target.value)} />
              </Field>
              <Field label="PLZ">
                <input value={form.zip} inputMode="numeric" disabled={off} onChange={(event) => set('zip', event.target.value)} />
              </Field>
              <Field label="Ort">
                <input value={form.city} disabled={off} onChange={(event) => set('city', event.target.value)} />
              </Field>
            </div>

            <span className="section-title">Zentraler Kontakt</span>
            <div className="form-grid">
              <Field label="E-Mail">
                <input type="email" value={form.contactEmail} disabled={off} onChange={(event) => set('contactEmail', event.target.value)} />
              </Field>
              <Field label="Telefon">
                <input value={form.contactPhone} disabled={off} onChange={(event) => set('contactPhone', event.target.value)} />
              </Field>
              <Field label="Zeitzone">
                <input value={form.timezone} disabled={off} onChange={(event) => set('timezone', event.target.value)} />
              </Field>
            </div>

            {isSuperadmin && (
              <MapKeyField tenantId={tenantId} hasKey={Boolean(detail.data?.has_maptiler_key)} onChanged={() => detail.reload()} notify={notify} />
            )}
          </div>
        </Card>
      )}

      {tab === 'contacts' && (
        <Card title="Ansprechpartner" actions={editButton('contacts')}>
          <ContactList
            tenantId={tenantId}
            contacts={detail.data?.contacts ?? []}
            loading={detail.loading}
            readOnly={editing !== 'contacts'}
            onChanged={changed}
            notify={notify}
          />
        </Card>
      )}

      {tab === 'locations' && (
        <Card title="Servicestandorte" actions={editButton('locations')}>
          <LocationList
            tenantId={tenantId}
            locations={detail.data?.locations ?? []}
            loading={detail.loading}
            readOnly={editing !== 'locations'}
            onChanged={changed}
            notify={notify}
          />
        </Card>
      )}

      {tab === 'users' && (
        <Card title="Benutzer" actions={linkButton('/admin/users', 'In der Benutzerverwaltung bearbeiten')}>
          <RelatedList
            loading={detail.loading}
            items={detail.data?.users ?? []}
            emptyTitle="Keine Benutzer"
            emptyHint="Dieser Organisation ist noch kein Konto zugeordnet."
            icon={<IconUsers width={16} height={16} />}
            tone="primary"
            title={(u: any) => `${u.first_name} ${u.last_name}`}
            subtitle={(u: any) => [u.email, u.job_title, u.role_names].filter(Boolean).join(' · ') || 'ohne Rollen'}
            badges={(u: any) => (
              <>
                {u.is_superadmin ? <Badge variant="danger">Superadmin</Badge> : null}
                {u.is_guest ? <Badge variant="warning">Gast</Badge> : null}
                {u.is_active ? null : <Badge>Deaktiviert</Badge>}
              </>
            )}
            href={(u: any) => `/admin/users/${u.id}`}
          />
        </Card>
      )}

      {tab === 'properties' && (
        <Card title="Objekte" actions={linkButton('/properties', 'In den Objektdaten bearbeiten')}>
          <RelatedList
            loading={detail.loading}
            items={detail.data?.properties ?? []}
            emptyTitle="Keine Objekte"
            emptyHint="Dieser Organisation ist noch keine Liegenschaft zugeordnet."
            icon={<IconBuilding width={16} height={16} />}
            tone="accent"
            title={(property: any) => property.name}
            subtitle={(property: any) =>
              [
                [property.street, [property.zip, property.city].filter(Boolean).join(' ')].filter(Boolean).join(', '),
                property.customer_name,
                property.units_count ? `${property.units_count} Einheiten` : null,
              ]
                .filter(Boolean)
                .join(' · ')
            }
            badges={(property: any) => (
              <>
                {property.open_tasks > 0 ? <Badge variant="warning">{property.open_tasks} offen</Badge> : null}
                {property.is_active ? null : <Badge>Inaktiv</Badge>}
              </>
            )}
            href={(property: any) => `/properties/${property.id}`}
          />
        </Card>
      )}

      {tab === 'gefahr' && darfGefahr && (
        <Card title="Gefahrenzone">
          <TenantDangerZone tenantId={tenantId} tenantName={detail.data?.name ?? form.name} />
        </Card>
      )}
    </>
  );
}

/**
 * Kartenschlüssel der Organisation.
 *
 * Der hinterlegte Wert wird nie zurückgeliefert – die Oberfläche erfährt
 * nur, ob einer gesetzt ist. Deshalb ein eigenes Feld mit eigenem Speichern
 * statt eines Eintrags im Stammdatenformular: Ein leeres Feld beim
 * gemeinsamen Speichern würde den Schlüssel sonst versehentlich löschen.
 */
function MapKeyField({
  tenantId,
  hasKey,
  onChanged,
  notify,
}: {
  tenantId: number;
  hasKey: boolean;
  onChanged: () => void;
  notify: (message: string, variant?: 'info' | 'success' | 'error') => void;
}) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async (key: string) => {
    setBusy(true);
    try {
      const result = await api.put(`/api/tenants/${tenantId}/maptiler-key`, { key });
      setValue('');
      onChanged();
      notify(result.hasKey ? 'Kartenschlüssel gespeichert.' : 'Kartenschlüssel entfernt.', 'success');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Speichern fehlgeschlagen.', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="record-form" style={{ borderColor: 'var(--border)', boxShadow: 'none' }}>
      <div className="row" style={{ gap: 8, marginBottom: 10 }}>
        <span className="icon-tile accent">
          <IconShield width={16} height={16} />
        </span>
        <div>
          <strong style={{ fontSize: 13.5 }}>Kartenschlüssel (MapTiler)</strong> <DsfaBadge id="maps.provider" />
          <div className="small subtle">
            Straßen- und Satellitenansicht der Tourenplanung. Nur für Superadmins.
          </div>
        </div>
        <span className="spacer" />
        <Badge variant={hasKey ? 'success' : ''}>{hasKey ? 'Hinterlegt' : 'Nicht hinterlegt'}</Badge>
      </div>

      <Field
        label={hasKey ? 'Schlüssel ersetzen' : 'Schlüssel hinterlegen'}
        hint="Der Schlüssel bleibt auf dem Server – Kacheln laufen über /api/maps, der Browser sieht ihn nie."
      >
        <input
          type="password"
          autoComplete="off"
          placeholder={hasKey ? '•••••••• (unverändert lassen)' : 'Schlüssel aus cloud.maptiler.com'}
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
      </Field>

      <div className="row end" style={{ marginTop: 10 }}>
        {hasKey && (
          <button
            className="btn sm ghost"
            disabled={busy}
            onClick={() => {
              if (window.confirm('Kartenschlüssel der Organisation entfernen?')) void save('');
            }}
          >
            <IconTrash width={13} height={13} /> Entfernen
          </button>
        )}
        <button className="btn sm primary" disabled={busy || value.trim().length < 8} onClick={() => save(value)}>
          Schlüssel speichern
        </button>
      </div>
    </div>
  );
}

/**
 * Zugeordnete Datensätze eines Mandanten (Benutzer, Objekte).
 *
 * Nur lesend mit Sprung in die jeweilige Fachseite: Benutzer- und
 * Objektpflege haben dort eigene, vollständige Masken. Sie im
 * Mandantendialog zu wiederholen hieße, zwei Stellen gleich halten zu
 * müssen. Der Dialog schließt beim Sprung, sonst bliebe er hinter der neuen
 * Seite offen stehen.
 */
function RelatedList({
  loading,
  items,
  emptyTitle,
  emptyHint,
  icon,
  tone,
  title,
  subtitle,
  badges,
  href,
}: {
  loading: boolean;
  items: any[];
  emptyTitle: string;
  emptyHint: string;
  icon: ReactNode;
  tone: 'primary' | 'accent';
  title: (item: any) => string;
  subtitle: (item: any) => string;
  badges: (item: any) => ReactNode;
  href: (item: any) => string;
}) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  if (loading) return <Loading />;

  const filtered = search.trim()
    ? items.filter((item) => `${title(item)} ${subtitle(item)}`.toLowerCase().includes(search.toLowerCase()))
    : items;

  if (items.length === 0) {
    return <EmptyState icon={icon} title={emptyTitle} hint={emptyHint} />;
  }

  return (
    <div className="stack" style={{ gap: 10 }}>
      {items.length > 6 && (
        <div style={{ position: 'relative' }}>
          <input
            type="search"
            placeholder="Suchen …"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            style={{ paddingLeft: 34 }}
          />
          <span style={{ position: 'absolute', left: 11, top: 11, color: 'var(--text-subtle)' }}>
            <IconSearch width={15} height={15} />
          </span>
        </div>
      )}

      {filtered.length === 0 && <EmptyState title="Kein Treffer" hint="Kein Eintrag passt zur Suche." />}

      {filtered.map((item) => (
        <button
          key={item.id}
          className="record-row as-button"
          onClick={() => navigate(href(item))}
          title="Zum Datensatz springen"
        >
          <span className={`icon-tile ${tone}`}>{icon}</span>
          <div style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
            <div className="row" style={{ gap: 8 }}>
              <strong className="truncate">{title(item)}</strong>
              {badges(item)}
            </div>
            <div className="small subtle truncate">{subtitle(item)}</div>
          </div>
          <IconChevronRight width={15} height={15} />
        </button>
      ))}
    </div>
  );
}

/* ---------------------------- Ansprechpartner ---------------------- */

function ContactList({
  tenantId,
  contacts,
  loading,
  readOnly,
  onChanged,
  notify,
}: {
  tenantId: number;
  contacts: any[];
  loading: boolean;
  readOnly: boolean;
  onChanged: () => void;
  notify: (message: string, variant?: 'info' | 'success' | 'error') => void;
}) {
  const [draft, setDraft] = useState<any | null>(null);

  const save = async (contact: any) => {
    try {
      if (contact.id) await api.patch(`/api/tenants/${tenantId}/contacts/${contact.id}`, contact);
      else await api.post(`/api/tenants/${tenantId}/contacts`, contact);
      setDraft(null);
      onChanged();
      notify('Ansprechpartner gespeichert.', 'success');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Speichern fehlgeschlagen.', 'error');
    }
  };

  const remove = async (contact: any) => {
    if (!window.confirm(`Ansprechpartner „${contact.name}“ löschen?`)) return;
    await api.delete(`/api/tenants/${tenantId}/contacts/${contact.id}`);
    onChanged();
    notify('Ansprechpartner gelöscht.', 'success');
  };

  if (loading) return <Loading />;

  return (
    <div className="stack" style={{ gap: 10 }}>
      {contacts.length === 0 && !draft && (
        <EmptyState
          icon={<IconUsers width={20} height={20} />}
          title="Noch kein Ansprechpartner"
          hint="Erfassen Sie Geschäftsführung, Disposition oder Objektleitung mit Funktion und Erreichbarkeit."
        />
      )}

      {contacts.map((contact) =>
        draft?.id === contact.id ? (
          <ContactForm key={contact.id} value={draft} onChange={setDraft} onSave={save} onCancel={() => setDraft(null)} />
        ) : (
          <div key={contact.id} className="record-row">
            <span className="icon-tile primary">
              <IconUsers width={16} height={16} />
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <strong>{contact.name}</strong>
              {contact.role && <span className="chip" style={{ marginLeft: 8 }}>{contact.role}</span>}
              <div className="small subtle">
                {[contact.phone, contact.email].filter(Boolean).join(' · ') || 'ohne Kontaktdaten'}
              </div>
            </div>
            {!readOnly && (
              <>
                <button className="btn sm" onClick={() => setDraft({ ...contact })}>
                  <IconEdit width={13} height={13} />
                </button>
                <button className="btn sm ghost" onClick={() => remove(contact)}>
                  <IconTrash width={13} height={13} />
                </button>
              </>
            )}
          </div>
        ),
      )}

      {draft && !draft.id && (
        <ContactForm value={draft} onChange={setDraft} onSave={save} onCancel={() => setDraft(null)} />
      )}

      {!draft && !readOnly && (
        <button className="btn" onClick={() => setDraft({ name: '', role: '', phone: '', email: '' })}>
          <IconPlus width={14} height={14} /> Ansprechpartner hinzufügen
        </button>
      )}
    </div>
  );
}

function ContactForm({
  value,
  onChange,
  onSave,
  onCancel,
}: {
  value: any;
  onChange: (value: any) => void;
  onSave: (value: any) => void;
  onCancel: () => void;
}) {
  return (
    <div className="record-form">
      <div className="form-grid">
        <Field label="Name" required>
          <input autoFocus value={value.name ?? ''} onChange={(event) => onChange({ ...value, name: event.target.value })} />
        </Field>
        <Field label="Funktion">
          <input
            value={value.role ?? ''}
            placeholder="z. B. Objektleitung"
            onChange={(event) => onChange({ ...value, role: event.target.value })}
          />
        </Field>
        <Field label="Telefon">
          <input value={value.phone ?? ''} onChange={(event) => onChange({ ...value, phone: event.target.value })} />
        </Field>
        <Field label="E-Mail">
          <input
            type="email"
            value={value.email ?? ''}
            onChange={(event) => onChange({ ...value, email: event.target.value })}
          />
        </Field>
      </div>
      <div className="row end" style={{ marginTop: 10 }}>
        <button className="btn sm" onClick={onCancel}>
          Abbrechen
        </button>
        <button className="btn sm primary" disabled={!value.name} onClick={() => onSave(value)}>
          Übernehmen
        </button>
      </div>
    </div>
  );
}

/* --------------------------- Servicestandorte ---------------------- */

function LocationList({
  tenantId,
  locations,
  loading,
  readOnly,
  onChanged,
  notify,
}: {
  tenantId: number;
  locations: any[];
  loading: boolean;
  readOnly: boolean;
  onChanged: () => void;
  notify: (message: string, variant?: 'info' | 'success' | 'error') => void;
}) {
  const [draft, setDraft] = useState<any | null>(null);

  const save = async (location: any) => {
    try {
      const payload = {
        ...location,
        latitude: location.latitude === '' || location.latitude == null ? null : Number(location.latitude),
        longitude: location.longitude === '' || location.longitude == null ? null : Number(location.longitude),
        houseNo: location.houseNo ?? location.house_no ?? null,
        isDefault: Boolean(location.isDefault ?? location.is_default),
      };
      if (location.id) await api.patch(`/api/tenants/${tenantId}/locations/${location.id}`, payload);
      else await api.post(`/api/tenants/${tenantId}/locations`, payload);
      setDraft(null);
      onChanged();
      notify('Standort gespeichert.', 'success');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Speichern fehlgeschlagen.', 'error');
    }
  };

  const remove = async (location: any) => {
    if (!window.confirm(`Standort „${location.name}“ löschen?`)) return;
    await api.delete(`/api/tenants/${tenantId}/locations/${location.id}`);
    onChanged();
    notify('Standort gelöscht.', 'success');
  };

  if (loading) return <Loading />;

  return (
    <div className="stack" style={{ gap: 10 }}>
      {locations.length === 0 && !draft && (
        <EmptyState
          icon={<IconPin width={20} height={20} />}
          title="Noch kein Standort"
          hint="Betriebshof, Lager oder Stützpunkt – Standorte dienen als Start- und Zielpunkt von Touren."
        />
      )}

      {locations.map((location) =>
        draft?.id === location.id ? (
          <LocationForm key={location.id} value={draft} onChange={setDraft} onSave={save} onCancel={() => setDraft(null)} />
        ) : (
          <div key={location.id} className="record-row">
            <span className="icon-tile success">
              <IconPin width={16} height={16} />
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="row" style={{ gap: 8 }}>
                <strong>{location.name}</strong>
                <span className="chip">{LOCATION_KINDS[location.kind] ?? location.kind}</span>
                {location.is_default ? <Badge variant="success">Vorgabe</Badge> : null}
                {location.is_active ? null : <Badge>Inaktiv</Badge>}
              </div>
              <div className="small subtle">
                {[[location.street, location.house_no].filter(Boolean).join(' '), [location.zip, location.city].filter(Boolean).join(' ')]
                  .filter(Boolean)
                  .join(', ') || 'ohne Anschrift'}
                {location.latitude != null && location.longitude != null
                  ? ` · ${Number(location.latitude).toFixed(4)} / ${Number(location.longitude).toFixed(4)}`
                  : ' · ohne Koordinaten'}
              </div>
            </div>
            {!readOnly && (
              <>
                <button
                  className="btn sm"
                  onClick={() => setDraft({ ...location, houseNo: location.house_no, isDefault: !!location.is_default, isActive: location.is_active !== 0 })}
                >
                  <IconEdit width={13} height={13} />
                </button>
                <button className="btn sm ghost" onClick={() => remove(location)}>
                  <IconTrash width={13} height={13} />
                </button>
              </>
            )}
          </div>
        ),
      )}

      {draft && !draft.id && (
        <LocationForm value={draft} onChange={setDraft} onSave={save} onCancel={() => setDraft(null)} />
      )}

      {!draft && !readOnly && (
        <button
          className="btn"
          onClick={() =>
            setDraft({ name: '', kind: 'depot', street: '', houseNo: '', zip: '', city: '', isDefault: false, isActive: true })
          }
        >
          <IconPlus width={14} height={14} /> Standort hinzufügen
        </button>
      )}
    </div>
  );
}

function LocationForm({
  value,
  onChange,
  onSave,
  onCancel,
}: {
  value: any;
  onChange: (value: any) => void;
  onSave: (value: any) => void;
  onCancel: () => void;
}) {
  const [locating, setLocating] = useState(false);

  /** Koordinaten aus der Anschrift ermitteln – ohne sie taugt der Standort nicht als Tourenpunkt. */
  const locate = async () => {
    const query = [value.street, value.houseNo, value.zip, value.city].filter(Boolean).join(' ');
    if (query.length < 5) return;
    setLocating(true);
    try {
      const hits = await api.get('/api/routing/geocode', { q: query });
      if (hits.length > 0) onChange({ ...value, latitude: hits[0].lat, longitude: hits[0].lng });
    } catch {
      /* Der Adressdienst ist optional – Koordinaten lassen sich auch eintippen. */
    } finally {
      setLocating(false);
    }
  };

  return (
    <div className="record-form">
      <div className="form-grid">
        <Field label="Bezeichnung" required>
          <input autoFocus value={value.name ?? ''} onChange={(event) => onChange({ ...value, name: event.target.value })} />
        </Field>
        <Field label="Art">
          <select value={value.kind ?? 'depot'} onChange={(event) => onChange({ ...value, kind: event.target.value })}>
            {Object.entries(LOCATION_KINDS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="address-grid" style={{ marginTop: 12 }}>
        <Field label="Straße">
          <input value={value.street ?? ''} onChange={(event) => onChange({ ...value, street: event.target.value })} />
        </Field>
        <Field label="Nr.">
          <input value={value.houseNo ?? ''} onChange={(event) => onChange({ ...value, houseNo: event.target.value })} />
        </Field>
        <Field label="PLZ">
          <input value={value.zip ?? ''} inputMode="numeric" onChange={(event) => onChange({ ...value, zip: event.target.value })} />
        </Field>
        <Field label="Ort">
          <input value={value.city ?? ''} onChange={(event) => onChange({ ...value, city: event.target.value })} />
        </Field>
      </div>

      <div className="form-grid" style={{ marginTop: 12 }}>
        <Field label="Breitengrad" hint="Für die Tourenplanung erforderlich.">
          <input
            type="number"
            step="0.00001"
            value={value.latitude ?? ''}
            onChange={(event) => onChange({ ...value, latitude: event.target.value })}
          />
        </Field>
        <Field label="Längengrad">
          <input
            type="number"
            step="0.00001"
            value={value.longitude ?? ''}
            onChange={(event) => onChange({ ...value, longitude: event.target.value })}
          />
        </Field>
        <Field label="Telefon">
          <input value={value.phone ?? ''} onChange={(event) => onChange({ ...value, phone: event.target.value })} />
        </Field>
      </div>

      <div className="row" style={{ marginTop: 12 }}>
        <button className="btn sm" onClick={locate} disabled={locating}>
          <IconSearch width={13} height={13} /> {locating ? 'Suche …' : 'Koordinaten aus Anschrift'}
        </button>
        <Toggle
          checked={Boolean(value.isDefault)}
          onChange={(checked) => onChange({ ...value, isDefault: checked })}
          label="Vorgabe der Organisation"
        />
        <Toggle
          checked={value.isActive !== false}
          onChange={(checked) => onChange({ ...value, isActive: checked })}
          label="Aktiv"
        />
        <span className="spacer" />
        <button className="btn sm" onClick={onCancel}>
          Abbrechen
        </button>
        <button className="btn sm primary" disabled={!value.name} onClick={() => onSave(value)}>
          Übernehmen
        </button>
      </div>
    </div>
  );
}
